import {
  useEffect,
  useId,
  useRef,
  type ReactNode,
  type RefObject,
} from 'react';
import { createPortal } from 'react-dom';

import { empilharNoTopo } from './camadaSuperior.js';
import { travarRolagem } from './travaDeRolagem.js';

import './folha.css';

export type PropsFolha = {
  aberta: boolean;
  /** Nome acessível do diálogo e cabeçalho visível: um texto só, para que
   *  o que se lê e o que se ouve não divirjam. */
  titulo: string;
  /** Onde o foco pousa ao abrir. Sem isto o foco fica no próprio
   *  `<dialog>` — o padrão do APG —, e não no primeiro focável da ordem do
   *  documento: o primeiro é sempre o botão "Fechar" do cabeçalho, de modo
   *  que abrir uma folha anunciava "Fechar, botão" com o conteúdo todo
   *  atrás. Quem tem um alvo melhor (o grupo de chips de Registrar) aponta
   *  para ele.
   *
   *  Precisa ser uma referência **estável** (a de `useRef`): ela entra nas
   *  dependências do efeito que abre a folha, e uma identidade nova a cada
   *  renderização refaria o `showModal`, a trava de rolagem e o `inert`. */
  focoInicial?: RefObject<HTMLElement | null>;
  /** Retorne false para recusar enquanto uma gravação estiver em curso. */
  aoFechar: () => void | false;
  children: ReactNode;
};

export function Folha({
  aberta,
  titulo,
  focoInicial,
  aoFechar,
  children,
}: PropsFolha) {
  const referencia = useRef<HTMLDialogElement>(null);
  // Um pedido aceito de fechamento por abertura. O `Escape` chega por dois caminhos
  // — `keydown` e `cancel` —, e os navegadores de hoje suprimem o `cancel`
  // depois do `preventDefault` do `keydown`; isso é comportamento de
  // implementação, não garantia da especificação, e os caminhos que não
  // passam pelo `keydown` não teriam essa proteção nenhuma. A trava é aqui,
  // não na confiança no navegador.
  const jaPediuParaFechar = useRef(false);
  const gerado = useId();
  const idTitulo = `folha${gerado}-titulo`;

  useEffect(() => {
    const dialogo = referencia.current;
    if (!aberta || dialogo === null) {
      // Fechou: o próximo ciclo de abertura pode pedir de novo.
      jaPediuParaFechar.current = false;
      return;
    }

    // Guardado antes de qualquer foco nosso: é para cá que o foco volta
    // quando a folha fecha, e sem isto quem navega por teclado ou VoiceOver
    // é devolvido ao começo da tela.
    const focoAnterior =
      document.activeElement instanceof HTMLElement
        ? document.activeElement
        : null;

    if (!dialogo.open) dialogo.showModal();
    const soltarRolagem = travarRolagem();
    // A folha se anuncia como o topo da tela: é aqui dentro que o `Toast`
    // escreve enquanto ela estiver aberta, porque no WebKit nada se sobrepõe
    // a um diálogo modal (ver `camadaSuperior.ts`).
    const soltarTopo = empilharNoTopo(dialogo);

    // O diálogo modal já torna o resto da página inerte nos navegadores que
    // implementam o `top layer`, mas o `inert` explícito é o que garante o
    // mesmo comportamento onde ele falta — e é o que o teste consegue ver.
    // Só marcamos quem ainda não estava inerte, para não roubar o atributo
    // de quem já o tinha por conta própria (duas folhas empilhadas).
    // Isto é um **retrato do momento de abrir**: filho do `body` acrescentado
    // depois — um `<div>` de biblioteca — não fica inerte. Um
    // `MutationObserver` resolveria, e espera o primeiro caso real; o `top
    // layer` do diálogo modal já cobre os navegadores que o implementam.
    //
    // O `Toast` fica de fora por referência explícita — pela classe, que é o
    // que os **dois** contêineres dele têm em comum: o do `body`, que é filho
    // do `body` e portanto candidato a esta varredura, e o de dentro da folha,
    // que já está fora dela por não ser filho do `body`. Ele é a única coisa
    // que **precisa** falar por cima de uma folha aberta — o erro do
    // salvamento que a própria folha tentou —, e o do `body` ainda por cima é
    // quem recebe a mensagem no instante em que a folha fecha. Torná-lo inerte
    // emudeceria a região viva e deixaria o "Desfazer" sem toque, teclado nem
    // VoiceOver.
    const inertados = Array.from(document.body.children).filter(
      (elemento) =>
        elemento !== dialogo &&
        !elemento.classList.contains('ds-toast') &&
        !elemento.hasAttribute('inert'),
    );
    for (const elemento of inertados) elemento.setAttribute('inert', '');

    // `preventScroll`: focar rola o ancestral rolável até o elemento, e com
    // texto de acessibilidade isso salta a folha para o meio antes mesmo de
    // ela terminar de subir.
    (focoInicial?.current ?? dialogo).focus({ preventScroll: true });

    return () => {
      soltarTopo();
      for (const elemento of inertados) elemento.removeAttribute('inert');
      soltarRolagem();
      if (dialogo.open) dialogo.close();
      // `focus` só depois de o fundo deixar de ser inerte: focar dentro de
      // uma subárvore inerte não faz nada, e o foco cairia no `body`.
      focoAnterior?.focus({ preventScroll: true });
    };
  }, [aberta, focoInicial]);

  function pedirParaFechar() {
    if (jaPediuParaFechar.current) return;
    // Recusar não consome o pedido: depois de uma falha de gravação,
    // Fechar, Escape e o fundo precisam continuar sendo saídas.
    if (aoFechar() !== false) jaPediuParaFechar.current = true;
  }

  if (!aberta) return null;

  return createPortal(
    // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions -- o diálogo é o próprio fundo escuro: tocar nele é o gesto de fechar do iOS, e o mesmo fechamento existe no botão e no Escape.
    <dialog
      ref={referencia}
      className="ds-folha"
      aria-labelledby={idTitulo}
      // Sem elemento focável nenhum dentro, o foco vai para o diálogo; sem
      // isto ele iria para o `body` e o Escape deixaria de funcionar.
      tabIndex={-1}
      onKeyDown={(evento) => {
        if (evento.key !== 'Escape') return;
        // `preventDefault` cancela o fechamento nativo: quem fecha é o
        // estado de quem chama, senão o `open` do DOM e o `aberta` do React
        // divergem e a folha não reabre.
        evento.preventDefault();
        pedirParaFechar();
      }}
      // Rede de segurança para os caminhos que não passam pelo `keydown`
      // (fechamento por gesto do sistema, por exemplo).
      onCancel={(evento) => {
        evento.preventDefault();
        pedirParaFechar();
      }}
      onClick={(evento) => {
        // O painel é filho: um clique nele tem `target` diferente do
        // diálogo, e só o fundo escuro fecha.
        if (evento.target === evento.currentTarget) pedirParaFechar();
      }}
    >
      {/* O trilho existe para ter altura automática: é ele que anima de `0fr`
          a `1fr`, e é por ele ter altura indefinida que a linha `1fr` resolve
          para o tamanho do conteúdo em vez de comer a tela toda. */}
      <div className="ds-folha__trilho">
        <div className="ds-folha__painel">
          {/* Decorativo: marca a borda de onde a folha sai. O arrasto ainda
            não existe — nasce na tela que primeiro precisar dele —, e por
            isso em texto de acessibilidade o CSS o tira do layout: ali cada
            pixel de cromo é pedaço de conteúdo à vista, e um puxador que
            ninguém pode puxar é o primeiro a sair. */}
          <div className="ds-folha__puxador" aria-hidden="true" />
          <header className="ds-folha__cabecalho">
            <h2 className="ds-folha__titulo" id={idTitulo}>
              {titulo}
            </h2>
            <button
              type="button"
              className="ds-folha__fechar"
              onClick={pedirParaFechar}
              aria-label="Fechar"
            >
              <svg
                viewBox="0 0 24 24"
                width="1em"
                height="1em"
                aria-hidden="true"
              >
                <path
                  d="M6 6 L18 18 M18 6 L6 18"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                />
              </svg>
            </button>
          </header>
          {/* A rolagem é aqui dentro, nunca no documento: com texto de
            acessibilidade o corpo passa da altura da tela, e cortar em vez
            de rolar esconderia o botão de salvar. */}
          <div className="ds-folha__corpo">{children}</div>
        </div>
      </div>
    </dialog>,
    document.body,
  );
}
